import { useState, useCallback } from 'react';
import { doc, writeBatch, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { deleteChatMemories } from '../../utils/memoryApi';

export const useMultiSelect = () => {
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState(new Set());

  const toggleSelectMode = useCallback(() => {
    setIsSelectMode(!isSelectMode);
    setSelectedChatIds(new Set());
  }, [isSelectMode]);

  const toggleChatSelection = useCallback((chatId, event) => {
    event.stopPropagation();
    const newSelected = new Set(selectedChatIds);
    if (newSelected.has(chatId)) {
      newSelected.delete(chatId);
    } else {
      newSelected.add(chatId);
    }
    setSelectedChatIds(newSelected);
  }, [selectedChatIds]);

  const selectAllChats = useCallback((displayedChats) => {
    const allChatIds = new Set(displayedChats.map(chat => chat.id));
    setSelectedChatIds(allChatIds);
  }, []);

  const deselectAllChats = useCallback(() => {
    setSelectedChatIds(new Set());
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedChatIds(new Set());
  }, []);

  const bulkArchiveChats = useCallback(async () => {
    if (selectedChatIds.size === 0) return;
    
    try {
      const batch = writeBatch(firestore);
      selectedChatIds.forEach(chatId => {
        const chatRef = doc(firestore, 'chats', chatId);
        batch.update(chatRef, { archived: true });
      });
      await batch.commit();
      exitSelectMode();
    } catch (error) {
      console.error('Error archiving chats:', error);
    }
  }, [selectedChatIds, exitSelectMode]);

  const bulkUnarchiveChats = useCallback(async () => {
    if (selectedChatIds.size === 0) return;
    
    try {
      const batch = writeBatch(firestore);
      selectedChatIds.forEach(chatId => {
        const chatRef = doc(firestore, 'chats', chatId);
        batch.update(chatRef, { archived: false });
      });
      await batch.commit();
      exitSelectMode();
    } catch (error) {
      console.error('Error unarchiving chats:', error);
    }
  }, [selectedChatIds, exitSelectMode]);

  const bulkDeleteChats = useCallback(async (setProgressModal) => {
    if (selectedChatIds.size === 0) return;
    
    try {
      const chatIds = Array.from(selectedChatIds);
      setProgressModal({ 
        visible: true, 
        progress: 0, 
        total: chatIds.length, 
        operation: 'Deleting chats...' 
      });
      
      const batchSize = 2;
      let processedCount = 0;
      
      for (let i = 0; i < chatIds.length; i += batchSize) {
        const batchChatIds = chatIds.slice(i, i + batchSize);
        const batch = writeBatch(firestore);
        
        for (const chatId of batchChatIds) {
          const chatRef = doc(firestore, 'chats', chatId);
          
          const messagesRef = collection(firestore, 'chats', chatId, 'messages');
          const messagesSnapshot = await getDocs(messagesRef);
          messagesSnapshot.forEach((doc) => {
            batch.delete(doc.ref);
          });
          
          batch.delete(chatRef);
        }
        
        await batch.commit();
        await Promise.allSettled(
          batchChatIds.map((chatId) => deleteChatMemories(chatId))
        );
        
        processedCount += batchChatIds.length;
        setProgressModal(prev => ({ 
          ...prev, 
          progress: processedCount 
        }));
        
        if (i + batchSize < chatIds.length) {
          await new Promise(resolve => setTimeout(resolve, 2));
        }
      }
      
      setProgressModal({ visible: false, progress: 0, total: 0, operation: '' });
      exitSelectMode();
    } catch (error) {
      console.error('Error deleting chats:', error);
      setProgressModal({ visible: false, progress: 0, total: 0, operation: '' });
    }
  }, [selectedChatIds, exitSelectMode]);

  return {
    isSelectMode,
    selectedChatIds,
    toggleSelectMode,
    toggleChatSelection,
    selectAllChats,
    deselectAllChats,
    exitSelectMode,
    bulkArchiveChats,
    bulkUnarchiveChats,
    bulkDeleteChats
  };
};
